// Persistent "Your Vehicle" bar, shown on every page once a vehicle has been selected via
// either the homepage selector (assets/ymm-filter.js) or collection/search pills
// (assets/vehicle-pills.js) -- both write to the same localStorage key and dispatch an
// 'es-vehicle-updated' event this file listens for. Uses `position:sticky` (not `fixed`) at the
// very top of <body>: sticky keeps its own place in normal document flow until you scroll past
// it, at which point it pins to the top of the viewport -- unlike `fixed`, which removes it
// from flow entirely and needed a manual body-margin-top hack to compensate that broke against
// this theme's several fixed/sticky header-vN variants (their own fixed positioning doesn't
// track a margin change on body, producing a visible gap). Sticky needs no such hack: it
// reserves its own height in the flow automatically, so there's no gap regardless of what the
// header itself does, while still staying pinned while scrolling.
(function(){
  const STORAGE_KEY = 'esSelectedVehicle';
  const UPDATE_EVENT = 'es-vehicle-updated';
  const BAR_ID = 'es-vehicle-bar';

  function getVehicle(){
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); }
    catch (_) { return null; }
  }

  function clearVehicle(){
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
    removeBar();
  }

  function removeBar(){
    const bar = document.getElementById(BAR_ID);
    if (bar) bar.remove();
  }

  function ensureStyle(){
    if (document.getElementById('es-vehicle-bar-style')) return;
    const style = document.createElement('style');
    style.id = 'es-vehicle-bar-style';
    style.textContent = `
      #${BAR_ID} { background:#12A8F6; position:sticky; top:0; z-index:999; }
      #${BAR_ID} .es-vb-inner { max-width:1400px; margin:0 auto; padding:7px 16px; display:flex; align-items:center; justify-content:center; gap:14px; flex-wrap:nowrap; }
      #${BAR_ID} a, #${BAR_ID} button { font-size:13px; line-height:1.3; flex:0 0 auto; }
      #${BAR_ID} .es-vb-label { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; min-width:0; flex:0 1 auto; }
      @media (max-width:640px){
        #${BAR_ID} .es-vb-inner { padding:4px 10px; gap:8px; }
        #${BAR_ID} a, #${BAR_ID} button { font-size:11px; }
      }
    `;
    document.head.appendChild(style);
  }

  function renderBar(vehicle){
    ensureStyle();
    let bar = document.getElementById(BAR_ID);
    if (!bar) {
      bar = document.createElement('div');
      bar.id = BAR_ID;
      document.body.insertBefore(bar, document.body.firstChild);
    }
    bar.innerHTML =
      '<div class="es-vb-inner">' +
        '<a href="' + vehicle.url + '" class="es-vb-label" style="color:#fff;text-decoration:none;letter-spacing:.06em;text-transform:uppercase;font-weight:600;">' +
          '<span style="opacity:.75;font-weight:400;">Your Vehicle:</span> ' + vehicle.label +
        '</a>' +
        '<button type="button" id="es-vehicle-bar-change" style="background:none;border:none;color:#fff;text-decoration:underline;opacity:.85;white-space:nowrap;cursor:pointer;padding:0;font:inherit;">Change</button>' +
        '<button type="button" id="es-vehicle-bar-close" aria-label="Remove vehicle" style="background:none;border:none;color:#fff;opacity:.7;line-height:1;cursor:pointer;padding:0;">×</button>' +
      '</div>';
    const closeBtn = document.getElementById('es-vehicle-bar-close');
    if (closeBtn) closeBtn.addEventListener('click', clearVehicle);
    const changeBtn = document.getElementById('es-vehicle-bar-change');
    // 2026-07-27 (Jeff): "Change" used to clear the saved vehicle and hard-navigate to the
    // homepage (the only place with a real selector at the time) -- now opens a small popover
    // right under this bar instead (assets/es-vehicle-popover.js), so the shopper can pick a new
    // vehicle without leaving the page they're already on.
    if (changeBtn) changeBtn.addEventListener('click', () => {
      if (window.ESVehiclePopover) window.ESVehiclePopover.toggle(changeBtn);
    });
  }

  function refresh(){
    const vehicle = getVehicle();
    if (vehicle && vehicle.label && vehicle.url) renderBar(vehicle);
    else removeBar();
  }

  // Exposed so ymm-filter.js and vehicle-pills.js can record a selection without this file
  // needing to know anything about how either of them resolves a vehicle. Dispatches an event
  // (rather than relying solely on the one-time render at script load) so the bar reflects the
  // CURRENT page's vehicle even when it's determined asynchronously (vehicle-pills.js resolves
  // its active make/model/generation only after its own /fitment-live fetch completes, which
  // happens after this script's own initial, synchronous localStorage read) -- without this,
  // the bar shows the PREVIOUS page's vehicle for a beat and then never updates, i.e. it's
  // always one step behind.
  window.ESVehicleMemory = {
    // `tags` (real product tags like ["vw","r","mk7"]) is stored alongside the display
    // label/URL so assets/vehicle-auto-filter.js can build a query against ANY page's own
    // scope without having to reverse-engineer a tag list back out of a URL string.
    save: function(label, url, tags){
      if (!label || !url) return;
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ label, url, tags: tags || [], ts: Date.now() })); } catch (_) {}
      document.dispatchEvent(new CustomEvent(UPDATE_EVENT));
    },
    clear: clearVehicle,
    // 2026-10-29 (Jeff): when the selector cannot find parts tagged for the exact choice it
    // widens (generation -> model -> make). Record that so the landing page can say so
    // instead of silently showing broader results. Only called when it actually widened.
    noteWiden: function(info){
      try { sessionStorage.setItem('esWiden', JSON.stringify(Object.assign({ ts: Date.now() }, info || {}))); } catch (_) {}
    }
  };

  function showWidenNotice(){
    let info = null;
    try { info = JSON.parse(sessionStorage.getItem('esWiden') || 'null'); sessionStorage.removeItem('esWiden'); } catch (_) {}
    if (!info || !info.want || !info.path || Date.now() - (info.ts || 0) > 120000) return;
    // Shopify re-orders the tag segments of a collection URL (vw+polo -> polo+vw), so compare
    // the tag SET, not the raw string.
    const norm = function(u){
      let x = ''; try { x = decodeURIComponent(String(u)).toLowerCase(); } catch (_) { x = String(u).toLowerCase(); }
      const m = x.match(/^(\/collections\/[^/?#]+)\/([^?#]+)/);
      if (m) return m[1] + '/' + m[2].replace(/ /g, '-').split('+').filter(Boolean).sort().join('+');
      return x.replace(/[?&]page=\d+/, '');
    };
    if (norm(info.path) !== norm(location.pathname + location.search)) return;
    const grid = document.getElementById('es-grid');
    if (!grid) return;
    const anchor = document.getElementById('es-vehicle-pills') || grid.closest('.content-box') || grid;
    if (!document.getElementById('es-widen-style')){
      const st = document.createElement('style');
      st.id = 'es-widen-style';
      st.textContent = '.es-widen{display:flex;align-items:flex-start;gap:10px;box-sizing:border-box;width:100%;max-width:920px;margin:0 auto 18px;padding:12px 14px 12px 16px;border:1px solid #dedede;border-left:3px solid #0070f3;border-radius:10px;background:#f6f9ff;font-family:Arial,sans-serif;font-size:14px;line-height:1.55;color:#1f1f1f;text-align:left}'
        + '.es-widen strong{color:#111}.es-widen__x{margin-left:auto;flex:0 0 auto;background:none;border:0;padding:0 2px;font-size:18px;line-height:1;color:#888;cursor:pointer}'
        + '@media (max-width:620px){.es-widen{font-size:13px;padding:10px 12px;margin-bottom:14px}}';
      document.head.appendChild(st);
    }
    const box = document.createElement('div');
    box.className = 'es-widen';
    box.setAttribute('role', 'status');
    const msg = document.createElement('span');
    const want = document.createElement('strong'); want.textContent = info.want;
    msg.appendChild(document.createTextNode('No parts are tagged for '));
    msg.appendChild(want);
    msg.appendChild(document.createTextNode(info.got ? ' yet, showing all ' + info.got + ' parts.' : ' yet, showing all parts.'));
    const x = document.createElement('button');
    x.type = 'button'; x.className = 'es-widen__x'; x.setAttribute('aria-label', 'Dismiss'); x.textContent = '×';
    x.addEventListener('click', function(){ box.remove(); });
    box.appendChild(msg); box.appendChild(x);
    anchor.parentNode.insertBefore(box, anchor);
  }

  document.addEventListener(UPDATE_EVENT, refresh);
  refresh();
  showWidenNotice();
})();
